import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Utensils, BedDouble } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { formatDDMMYYYY } from '../../utils/date';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SgContratos() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [contratos, setContratos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [modal, setModal] = useState(null); // null | {mode:'create'|'edit', data?}
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, e] = await Promise.all([
        axios.get(`${API}/servicios-generales/contratos`, { headers }),
        axios.get(`${API}/empresas-colaboradoras`, { headers }),
      ]);
      setContratos(c.data);
      setEmpresas(e.data);
    } catch { }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/servicios-generales/contratos/${confirmDel.id}`, { headers });
      toast.success('Contrato eliminado.');
      setConfirmDel(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const vigenciaBadge = (v) => {
    const map = {
      vigente: 'bg-emerald-100 text-emerald-700',
      pendiente: 'bg-blue-100 text-blue-700',
      vencido: 'bg-slate-100 text-slate-500',
      inactivo: 'bg-red-100 text-red-700',
    };
    return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${map[v] || 'bg-slate-100'}`}>{v.toUpperCase()}</span>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Contratos Comerciales</h1>
          <p className="text-sm text-slate-500">Gestiona los contratos por empresa contratista y sus habilitaciones.</p>
        </div>
        <button onClick={() => setModal({ mode: 'create' })}
          className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold rounded-lg"
          data-testid="btn-nuevo-contrato">
          <Plus className="w-4 h-4" />Nuevo Contrato
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">N° Contrato</th>
              <th className="text-left px-4 py-2 font-semibold">Empresa</th>
              <th className="text-left px-4 py-2 font-semibold">Vigencia</th>
              <th className="text-center px-4 py-2 font-semibold">Casino</th>
              <th className="text-center px-4 py-2 font-semibold">Hotelería</th>
              <th className="text-center px-4 py-2 font-semibold">Trabajadores</th>
              <th className="text-center px-4 py-2 font-semibold">Estado</th>
              <th className="text-right px-4 py-2 font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {contratos.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-slate-400 py-8">Sin contratos. Crea el primero.</td></tr>
            ) : contratos.map(c => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-2 font-mono text-xs font-semibold">{c.numero_contrato}</td>
                <td className="px-4 py-2 text-slate-700">{c.empresa_nombre}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{formatDDMMYYYY(c.fecha_inicio)} → {formatDDMMYYYY(c.fecha_termino)}</td>
                <td className="px-4 py-2 text-center">
                  {c.casino_activo ? <Utensils className="w-4 h-4 text-emerald-600 inline" /> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-2 text-center">
                  {c.hoteleria_activo ? <BedDouble className="w-4 h-4 text-emerald-600 inline" /> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-2 text-center text-xs font-semibold">{c.trabajadores}</td>
                <td className="px-4 py-2 text-center">{vigenciaBadge(c.vigencia)}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => setModal({ mode: 'edit', data: c })}
                    className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded" title="Editar">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => setConfirmDel(c)}
                    className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded" title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && <ModalContrato modal={modal} empresas={empresas} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} headers={headers} />}
      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-2">Eliminar contrato</h3>
            <p className="text-sm text-slate-600 mb-4">¿Confirmas eliminar el contrato <strong>{confirmDel.numero_contrato}</strong>?</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-3 py-2 text-sm border rounded-lg">Cancelar</button>
              <button onClick={handleDelete} className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg font-semibold">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ModalContrato({ modal, empresas, onClose, onSaved, headers }) {
  const isEdit = modal.mode === 'edit';
  const d = modal.data;
  const [form, setForm] = useState({
    empresa_id: d?.empresa_id || '',
    numero_contrato: d?.numero_contrato || '',
    descripcion: d?.descripcion || '',
    fecha_inicio: d?.fecha_inicio || '',
    fecha_termino: d?.fecha_termino || '',
    casino_activo: d?.casino_activo || false,
    hoteleria_activo: d?.hoteleria_activo || false,
    activo: d?.activo !== false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.empresa_id || !form.numero_contrato || !form.fecha_inicio || !form.fecha_termino) {
      toast.error('Completa todos los campos obligatorios'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, empresa_id: Number(form.empresa_id) };
      if (isEdit) {
        await axios.put(`${API}/servicios-generales/contratos/${d.id}`, payload, { headers });
        toast.success('Contrato actualizado.');
      } else {
        // trabajadores_ruts NO enviado → backend asigna todos los trabajadores de la empresa
        await axios.post(`${API}/servicios-generales/contratos`, payload, { headers });
        toast.success('Contrato creado.');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">{isEdit ? 'Editar' : 'Nuevo'} Contrato</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-slate-500 font-semibold">Empresa contratista *</label>
            <select value={form.empresa_id} onChange={e => setForm({ ...form, empresa_id: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="ct-empresa">
              <option value="">Selecciona una empresa</option>
              {empresas.map(e => <option key={e.id} value={e.id}>{e.razon_social}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">N° Contrato *</label>
            <div className="flex items-stretch gap-1">
              <input value={form.numero_contrato} onChange={e => setForm({ ...form, numero_contrato: e.target.value })}
                placeholder="45123456" className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg font-mono" data-testid="ct-numero" />
              <button type="button" onClick={async () => {
                try {
                  const r = await axios.get(`${API}/servicios-generales/contratos/sugerir-numero`, { headers });
                  setForm(f => ({ ...f, numero_contrato: r.data.numero_contrato }));
                  toast.success(`Sugerido: ${r.data.numero_contrato}`);
                } catch { toast.error('No se pudo sugerir número'); }
              }} className="px-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 hover:bg-emerald-100 rounded-lg font-semibold whitespace-nowrap"
                 data-testid="ct-sugerir-numero" title="Sugerir número disponible (45XXXXXX)">
                Sugerir
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Activo</label>
            <select value={form.activo ? '1' : '0'} onChange={e => setForm({ ...form, activo: e.target.value === '1' })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg">
              <option value="1">Sí</option>
              <option value="0">No</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500 font-semibold">Descripción</label>
            <input value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Fecha inicio *</label>
            <input type="date" value={form.fecha_inicio} onChange={e => setForm({ ...form, fecha_inicio: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="ct-inicio" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Fecha término *</label>
            <input type="date" value={form.fecha_termino} onChange={e => setForm({ ...form, fecha_termino: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="ct-termino" />
          </div>
          <label className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={form.casino_activo} onChange={e => setForm({ ...form, casino_activo: e.target.checked })}
              className="w-4 h-4" data-testid="ct-casino" />
            <Utensils className="w-4 h-4 text-emerald-700" />
            <span className="text-sm font-semibold">Casino habilitado</span>
          </label>
          <label className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={form.hoteleria_activo} onChange={e => setForm({ ...form, hoteleria_activo: e.target.checked })}
              className="w-4 h-4" data-testid="ct-hoteleria" />
            <BedDouble className="w-4 h-4 text-emerald-700" />
            <span className="text-sm font-semibold">Hotelería habilitada</span>
          </label>
          {!isEdit && (
            <p className="col-span-2 text-[11px] text-slate-500 bg-blue-50 p-2 rounded">
              💡 Al crear el contrato se asignarán automáticamente todos los trabajadores actualmente vinculados a esta empresa.
            </p>
          )}
          <div className="col-span-2 flex justify-end gap-2 mt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Cancelar</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg font-semibold disabled:opacity-50"
              data-testid="ct-submit">
              {saving ? 'Guardando...' : (isEdit ? 'Guardar' : 'Crear')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
